import { useCallback, type ChangeEvent, type FunctionComponent } from 'react';
import { useIntl } from 'react-intl';

import { boardSwitcherSharedState } from '@trello/board-switcher';
import { Button } from '@trello/nachos/button';
import { PopoverSection } from '@trello/nachos/experimental-popover-section';
import { GearIcon } from '@trello/nachos/icons/gear';
import { Popover, usePopover } from '@trello/nachos/popover';
import { PopoverMenu, PopoverMenuRadio } from '@trello/nachos/popover-menu';
import { Tooltip } from '@trello/nachos/tooltip';
import { useSharedStateSelector } from '@trello/shared-state';
import { useSplitScreenSharedState } from '@trello/split-screen';

import styles from './SettingsPopoverButton.less';

export const SettingsPopoverButton: FunctionComponent = () => {
  const intl = useIntl();
  const { triggerRef, toggle, hide, popoverProps } = usePopover();
  const currentMode = useSharedStateSelector(
    boardSwitcherSharedState,
    useCallback((state) => state.mode, []),
  );

  const { configuration: splitScreenConfiguration } =
    useSplitScreenSharedState();

  const settingsLabel = intl.formatMessage({
    id: 'templates.board_switcher.settings',
    defaultMessage: 'Settings',
    description: 'Label for the settings button in the board switcher',
  });

  const modalLabel = intl.formatMessage({
    id: 'templates.board_switcher.modal',
    defaultMessage: 'Modal',
    description: 'Label for the modal setting in the board switcher settings',
  });

  const dockedPanelLabel = intl.formatMessage({
    id: 'templates.board_switcher.docked-panel',
    defaultMessage: 'Docked panel',
    description:
      'Label for the docked panel setting in the board switcher settings',
  });

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const mode = event.target.value as 'modal' | 'panel';
      const layout = mode === 'panel' ? 'list' : 'grid';

      //if we don't hide the popover, it will stay open and float in space since its anchor point has moved
      hide();

      // TODO: Add analytics

      boardSwitcherSharedState.setValue({
        mode,
        layout,
      });
    },
    [hide],
  );

  if (splitScreenConfiguration === 'tabs') {
    //don't render the settings button on small screens
    return null;
  }

  return (
    <div key="settings-button">
      <Tooltip content={settingsLabel} hideTooltipOnClick>
        <Button
          className={styles.button}
          ref={triggerRef}
          onClick={toggle}
          aria-label={settingsLabel}
          iconBefore={<GearIcon size="medium" label={settingsLabel} />}
        />
      </Tooltip>
      <Popover {...popoverProps}>
        <PopoverSection title="Show switcher as">
          <PopoverMenu>
            <PopoverMenuRadio
              title={modalLabel}
              value="modal"
              name="switcher"
              defaultChecked={currentMode === 'modal'}
              onChange={onChange}
            />
            <PopoverMenuRadio
              title={dockedPanelLabel}
              value="panel"
              name="switcher"
              defaultChecked={currentMode === 'panel'}
              onChange={onChange}
            />
          </PopoverMenu>
        </PopoverSection>
      </Popover>
    </div>
  );
};
